import { useEffect, useRef, useState, MutableRefObject } from 'react';
import { supabase } from '@os/integrations/supabase/client';

type EmulationAnalyticsOptions = {
  appName: string;
  appPath?: string;
  enabled: boolean;
  screenshotsEnabled?: boolean;
  flushIntervalMs?: number;     // default 2000ms
  mouseThrottleMs?: number;     // default 400ms
};

type AnalyticsEvent = {
  type: 'emulation_input' | 'emulation_event';
  data: Record<string, any>;
};

export const useEmulationAnalytics = (opts: EmulationAnalyticsOptions) => {
  const {
    appName,
    appPath: initialPath = '',
    enabled,
    screenshotsEnabled = false,
    flushIntervalMs = 2000,
    mouseThrottleMs = 400,
  } = opts;

  const [userId, setUserId] = useState<string | null>(null);
  const [appPath, setAppPath] = useState<string>(initialPath);
  const containerRef = useRef<HTMLElement | null>(null);
  const lastMouseLoggedAtRef = useRef<number>(0);
  const queueRef = useRef<AnalyticsEvent[]>([]);
  const flushTimerRef = useRef<number | null>(null);

  // Resolve user_id once and on auth changes
  useEffect(() => {
    const init = async () => {
      const { data } = await supabase.auth.getSession();
      setUserId(data.session?.user?.id ?? null);
    };
    init();
    const { data } = supabase.auth.onAuthStateChange((_evt, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  // Insert batched events to Supabase
  const flush = async () => {
    if (!enabled || !userId || queueRef.current.length === 0) return;

    const payload = queueRef.current.map((ev) => ({
      user_id: userId,
      event_type: ev.type,
      event_data: {
        appName,
        appPath,
        ...ev.data,
      },
      user_agent: globalThis.navigator?.userAgent,
      screen_width: globalThis.window?.innerWidth,
      screen_height: globalThis.window?.innerHeight,
    }));

    queueRef.current = [];
    await supabase.from('usage_analytics').insert(payload);
  };

  // Start/stop flusher based on "enabled"
  useEffect(() => {
    if (!enabled) {
      if (flushTimerRef.current) {
        window.clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      queueRef.current = [];
      return;
    }
    if (!flushTimerRef.current) {
      flushTimerRef.current = window.setInterval(() => {
        flush().catch(() => {
          // Allow errors to bubble up via Supabase logs; keep app stable
        });
      }, flushIntervalMs);
    }
    return () => {
      if (flushTimerRef.current) {
        window.clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, [enabled, flushIntervalMs, userId, appName, appPath]);

  // Attach listeners to container
  useEffect(() => {
    const el = containerRef.current;
    if (!enabled || !el) return;

    const getRelative = (e: MouseEvent | WheelEvent) => {
      const rect = el.getBoundingClientRect();
      return {
        x: (e as MouseEvent).clientX - rect.left,
        y: (e as MouseEvent).clientY - rect.top,
      };
    };

    const onMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastMouseLoggedAtRef.current < mouseThrottleMs) return;
      lastMouseLoggedAtRef.current = now;
      const rel = getRelative(e);
      queueRef.current.push({
        type: 'emulation_input',
        data: { input: { kind: 'mousemove', ...rel }, ts: new Date().toISOString() },
      });
    };

    const onMouseDown = (e: MouseEvent) => {
      const rel = getRelative(e);
      queueRef.current.push({
        type: 'emulation_input',
        data: { input: { kind: 'mousedown', button: e.button, ...rel }, ts: new Date().toISOString() },
      });
    };

    const onMouseUp = (e: MouseEvent) => {
      const rel = getRelative(e);
      queueRef.current.push({
        type: 'emulation_input',
        data: { input: { kind: 'mouseup', button: e.button, ...rel }, ts: new Date().toISOString() },
      });
    };

    const onWheel = (e: WheelEvent) => {
      const rel = getRelative(e);
      queueRef.current.push({
        type: 'emulation_input',
        data: { input: { kind: 'wheel', deltaX: e.deltaX, deltaY: e.deltaY, ...rel }, ts: new Date().toISOString() },
      });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      queueRef.current.push({
        type: 'emulation_input',
        data: { input: { kind: 'keydown', key: e.key, code: e.code }, ts: new Date().toISOString() },
      });
    };

    const onKeyUp = (e: KeyboardEvent) => {
      queueRef.current.push({
        type: 'emulation_input',
        data: { input: { kind: 'keyup', key: e.key, code: e.code }, ts: new Date().toISOString() },
      });
    };

    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('keyup', onKeyUp);

    // Make sure element can receive key events
    el.setAttribute('tabindex', el.getAttribute('tabindex') ?? '0');

    return () => {
      el.removeEventListener('mousemove', onMouseMove);
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('keyup', onKeyUp);
    };
  }, [enabled, mouseThrottleMs, appName, appPath]);

  // Public API
  const setContainer = (refOrEl: MutableRefObject<HTMLElement | null> | HTMLElement | null) => {
    containerRef.current = (refOrEl && 'current' in (refOrEl as any)) ? (refOrEl as any).current : (refOrEl as HTMLElement | null);
  };

  const logCustomEvent = (name: string, extra: Record<string, any> = {}) => {
    if (!enabled) return;
    queueRef.current.push({
      type: 'emulation_event',
      data: { event: name, ...extra, ts: new Date().toISOString() },
    });
  };

  const captureScreenshot = () => {
    if (!enabled || !screenshotsEnabled) return;
    const el = containerRef.current;
    if (!el) return;

    // Try to capture the first canvas inside the container (js-dos and many emulators use canvas)
    const canvas = el.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) {
      // If no canvas, log that capture wasn't possible
      queueRef.current.push({
        type: 'emulation_event',
        data: { event: 'screenshot_unavailable', reason: 'no_canvas', ts: new Date().toISOString() },
      });
      return;
    }
    const dataUrl = canvas.toDataURL('image/jpeg', 0.6); // compress to reduce payload
    queueRef.current.push({
      type: 'emulation_event',
      data: { event: 'screenshot_capture', screenshot_data_url: dataUrl, ts: new Date().toISOString() },
    });
  };

  return {
    setContainer,
    setAppPath,
    logCustomEvent,
    captureScreenshot,
  };
};